import React from 'react';
import { Paper, Stack, Typography, Grid, Box, Tooltip } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CalculateIcon from '@mui/icons-material/Calculate';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatMoney } from "../producer-dashboard/ProducerDashboardHelper";

type GlobalScWalletSummaryProps = {
    currency: string;
    scBalance: number;
    isScApiError: boolean;
    totalScCredits: number;
    totalAwaitedPayout: number;
    projectedScBalance: number;
    isScRisk: boolean;
};

export const GlobalScWalletSummary = ({
    currency, scBalance, isScApiError, totalScCredits, totalAwaitedPayout, projectedScBalance, isScRisk
}: GlobalScWalletSummaryProps) => {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 4, mb: 4, borderRadius: 4, border: '1px solid',
                borderColor: isScRisk ? '#fca5a5' : '#e2e8f0',
                bgcolor: isScRisk ? '#fef2f2' : '#f8fafc'
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                <AccountBalanceWalletIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '1px' }}>
                    GLOBAL SC WALLET • {currency}
                </Typography>
            </Stack>

            <Grid container spacing={4} alignItems="center">
                <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Current SC Balance</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: isScApiError ? 'error.main' : '#0f172a' }}>
                        {isScApiError ? "API Error" : formatMoney(scBalance, currency)}
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <Stack spacing={2} sx={{ borderLeft: '1px solid #cbd5e1', borderRight: { md: '1px solid #cbd5e1' }, px: { md: 3 } }}>
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TrendingUpIcon fontSize="small" color="success" /> Expected Credits (In)
                            </Typography>
                            <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>
                                +{formatMoney(totalScCredits, currency)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TrendingDownIcon fontSize="small" color="error" /> 
                                Awaited Payouts (Out)
                                <Tooltip title="Includes all upcoming payouts regardless of their current status (Ready to process, Awaiting dependencies, or Blocked)." arrow placement="top">
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'pointer', ml: 0.5 }} />
                                </Tooltip>
                            </Typography>
                            <Typography variant="h5" color="error.main" sx={{ fontWeight: 800 }}>
                                -{formatMoney(totalAwaitedPayout, currency)}
                            </Typography>
                        </Box>
                    </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalculateIcon fontSize="small" /> Projected Balance
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: isScRisk ? 'error.main' : 'primary.main' }}>
                        {formatMoney(projectedScBalance, currency)}
                    </Typography>
                    {isScRisk && (
                        <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>
                            Insufficient funds for upcoming payouts.
                        </Typography>
                    )}
                </Grid>
            </Grid>
        </Paper>
    );
};